import { abweisen, ereignis, type Ereignis } from "../gemeinsam/index.js";
import { DJ_EREIGNIS as E } from "./ereignisse.js";
import { ANSPRACHEN, istWildart, text, type Wildart } from "./werte.js";

/**
 * Aggregat Freigabe (Domänenmodell §5): je Drückjagd und Revier. Die Zahl der Erlegungen gehört nicht hierher, sie
 * entsteht im Lesemodell Kontingentstand ({@link kontingentstand}).
 */
export type Kennzeichen = "automatisch" | "von Hand";

export interface Kontingent {
  wildart: Wildart;
  ansprachen: string[];
  stueckzahl: number;
  gesperrt: Kennzeichen | null;
}

export interface Freigabeposition {
  wildart: Wildart;
  freigabetext: string | null;
  meldepflicht: boolean;
  gesperrt: boolean;
}

export interface FreigabeZustand {
  festgelegt: boolean;
  positionen: Freigabeposition[];
  kontingente: Record<string, Kontingent>;
}

export const KEINE_FREIGABE: FreigabeZustand = { festgelegt: false, positionen: [], kontingente: {} };

export function freigabeAnwenden(z: FreigabeZustand, e: Ereignis): FreigabeZustand {
  const d = e.daten;
  switch (e.typ) {
    case E.FreigabeFestgelegt: {
      const positionen = ((d["Freigabepositionen"] as Array<Record<string, unknown>>) ?? []).map((p) => ({
        wildart: p["Wildart"] as Wildart, freigabetext: (p["Freigabetext"] as string | null) ?? null, meldepflicht: p["Meldepflicht"] === true,
        gesperrt: p["Freigabestatus"] === "gesperrt",
      }));
      const kontingente: Record<string, Kontingent> = {};
      for (const k of (d["Kontingente"] as Array<Record<string, unknown>>) ?? []) {
        const name = String(k["Kontingent"]);
        kontingente[name] = {
          wildart: k["Wildart"] as Wildart, ansprachen: (k["Ansprachen"] as string[]) ?? [], stueckzahl: Number(k["Stückzahl"]),
          gesperrt: z.kontingente[name]?.gesperrt ?? null,
        };
      }
      return { festgelegt: true, positionen, kontingente };
    }
    case E.FreigabeGesperrt:
    case E.SperreAufgehoben:
      return { ...z, positionen: z.positionen.map((p) => (p.wildart === d["Wildart"] ? { ...p, gesperrt: e.typ === E.FreigabeGesperrt } : p)) };
    case E.KontingentGesperrt:
    case E.KontingentsperreAufgehoben:
    case E.StueckzahlGeaendert: {
      const name = String(d["Kontingent"]);
      const k = z.kontingente[name];
      if (!k) return z;
      const neu = e.typ === E.KontingentGesperrt ? { ...k, gesperrt: d["Kennzeichen"] as Kennzeichen }
        : e.typ === E.KontingentsperreAufgehoben ? { ...k, gesperrt: null } : { ...k, stueckzahl: Number(d["Stückzahl neu"]) };
      return { ...z, kontingente: { ...z.kontingente, [name]: neu } };
    }
    default:
      return z;
  }
}

function stueckzahl(w: unknown): number {
  if (typeof w !== "number" || !Number.isInteger(w)) abweisen("unzulaessige-angaben");
  if (w < 0) abweisen("stueckzahl-negativ");
  return w;
}

/** Freigabe festlegen: Positionen je Wildart mit Kontingenten; gesetzte Sperren bleiben bei gleichem Kontingent. */
export function freigabeFestlegen(z: FreigabeZustand, positionenRoh: unknown, wildartenDerDrueckjagd: readonly string[], handelnd: string): Ereignis[] {
  if (!Array.isArray(positionenRoh)) abweisen("unzulaessige-angaben");
  const gesehen = new Set<string>();
  const namen = new Set<string>();
  const positionen: Array<Record<string, unknown>> = [];
  const kontingente: Array<Record<string, unknown>> = [];
  for (const p of positionenRoh as Array<Record<string, unknown>>) {
    const w = p?.["Wildart"];
    if (!istWildart(w)) abweisen("unzulaessige-angaben");
    if (gesehen.has(w)) abweisen("wildart-doppelt");
    gesehen.add(w);
    if (!wildartenDerDrueckjagd.includes(w)) abweisen("wildart-nicht-in-drueckjagd");
    const status = p["Freigabestatus"] ?? "frei";
    if (status !== "frei" && status !== "gesperrt") abweisen("unzulaessige-angaben");
    positionen.push({ Wildart: w, Freigabetext: text(p["Freigabetext"]), Meldepflicht: p["Meldepflicht"] === true, Freigabestatus: status });
    const ks = p["Kontingente"] ?? [];
    if (!Array.isArray(ks)) abweisen("unzulaessige-angaben");
    for (const k of ks as Array<Record<string, unknown>>) {
      const name = text(k?.["Kontingent"]);
      if (!name || namen.has(name)) abweisen("unzulaessige-angaben");
      namen.add(name);
      const anzahl = stueckzahl(k["Stückzahl"]);
      const ansprachen = k["Ansprachen"];
      if (!Array.isArray(ansprachen) || ansprachen.length === 0) abweisen("ansprache-unzulaessig");
      for (const a of ansprachen) if (!ANSPRACHEN[w].includes(String(a))) abweisen("ansprache-unzulaessig");
      kontingente.push({ Wildart: w, Kontingent: name, Ansprachen: [...new Set(ansprachen.map(String))], "Stückzahl": anzahl });
    }
  }
  return [ereignis(E.FreigabeFestgelegt, {
    Wildarten: positionen.map((p) => p["Wildart"]), Freigabepositionen: positionen, Kontingente: kontingente, "handelnde Person": handelnd,
  })];
}

function position(z: FreigabeZustand, wildart: unknown): Freigabeposition {
  const p = z.positionen.find((x) => x.wildart === wildart);
  if (!p) abweisen("unzulaessige-angaben");
  return p;
}

function kontingent(z: FreigabeZustand, name: unknown): Kontingent {
  const k = typeof name === "string" ? z.kontingente[name] : undefined;
  if (!k) abweisen("unzulaessige-angaben");
  return k;
}

export function freigabepositionSperren(z: FreigabeZustand, wildart: unknown, handelnd: string, jetzt: Date): Ereignis[] {
  const p = position(z, wildart);
  if (p.gesperrt) return [];
  return [ereignis(E.FreigabeGesperrt, { Wildart: p.wildart, Kennzeichen: "von Hand", Zeitpunkt: jetzt, "handelnde Person": handelnd })];
}

export function sperreAufheben(z: FreigabeZustand, wildart: unknown, handelnd: string, jetzt: Date): Ereignis[] {
  const p = position(z, wildart);
  if (!p.gesperrt) return [];
  return [ereignis(E.SperreAufgehoben, { Wildart: p.wildart, Zeitpunkt: jetzt, "handelnde Person": handelnd })];
}

/** Kontingent sperren: von Hand (Person) oder automatisch (System, `handelnde Person` = null, Policy P-04). */
export function kontingentSperren(z: FreigabeZustand, name: unknown, handelnd: string | null, jetzt: Date): Ereignis[] {
  const k = kontingent(z, name);
  if (k.gesperrt) return [];
  return [ereignis(E.KontingentGesperrt, {
    Kontingent: name, Kennzeichen: handelnd ? "von Hand" : "automatisch", Zeitpunkt: jetzt, "handelnde Person": handelnd,
  })];
}

/** Eine Kontingentsperre hebt nur eine Person auf, nie das System (F-143, ADR-0018). */
export function kontingentsperreAufheben(z: FreigabeZustand, name: unknown, handelnd: string, jetzt: Date): Ereignis[] {
  const k = kontingent(z, name);
  if (!k.gesperrt) return [];
  return [ereignis(E.KontingentsperreAufgehoben, { Kontingent: name, Zeitpunkt: jetzt, "handelnde Person": handelnd })];
}

export function stueckzahlAendern(z: FreigabeZustand, name: unknown, neu: unknown, handelnd: string, jetzt: Date): Ereignis[] {
  const k = kontingent(z, name);
  const n = stueckzahl(neu);
  if (n === k.stueckzahl) return [];
  return [ereignis(E.StueckzahlGeaendert, {
    Kontingent: name, "Stückzahl alt": k.stueckzahl, "Stückzahl neu": n, Zeitpunkt: jetzt, "handelnde Person": handelnd,
  })];
}

/** Passt eine Ansprache (Wildart, Altersklasse) zu einem Kontingent? Wildart und Altersklasse bzw. die Wildart selbst. */
export function passtZuKontingent(k: Pick<Kontingent, "wildart" | "ansprachen">, wildart: unknown, altersklasse: unknown): boolean {
  if (k.wildart !== wildart) return false;
  if (k.ansprachen.includes(String(wildart))) return true;
  return typeof altersklasse === "string" && k.ansprachen.includes(altersklasse);
}

export interface Ansprache {
  wildart: unknown;
  altersklasse: unknown;
}

/**
 * Grundsatz „Kalb vor Alttier“ (F-146): Wird ein Alttier gemeldet, weist die App die meldende Person darauf hin
 * (`drueckjagd/07`). Ein Alttier gibt es bei den Wildarten mit der Altersklasse „Alttier“ (Rotwild, Damwild).
 */
export function kalbVorAlttier(a: Ansprache): boolean {
  return a.altersklasse === "Alttier" && (ANSPRACHEN[a.wildart as Wildart] as readonly string[] | undefined)?.includes("Alttier") === true;
}

export interface Kontingentstand {
  kontingent: string;
  wildart: Wildart;
  ansprachen: string[];
  stueckzahl: number;
  erlegt: number;
  rest: number;
  offen: number;
  status: "frei" | "automatisch gesperrt" | "von Hand gesperrt";
  anzeige: string | null;
}

/** Kontingentstand (Lesemodell, F-140, F-145): Rest = Stückzahl − passende Erlegungen; offene Anschüsse nur angezeigt. */
export function kontingentstand(z: FreigabeZustand, erlegungen: readonly Ansprache[], offeneAnschuesse: readonly Ansprache[]): Kontingentstand[] {
  return Object.entries(z.kontingente).map(([name, k]) => {
    const erlegt = erlegungen.filter((e) => passtZuKontingent(k, e.wildart, e.altersklasse)).length;
    const offen = offeneAnschuesse.filter((e) => passtZuKontingent(k, e.wildart, e.altersklasse)).length;
    const rest = k.stueckzahl - erlegt;
    const anzeige = offen > 0 ? `noch ${Math.max(rest, 0)} frei, ${offen} ${offen === 1 ? "Anschuss" : "Anschüsse"} offen` : null;
    return {
      kontingent: name, wildart: k.wildart, ansprachen: k.ansprachen, stueckzahl: k.stueckzahl, erlegt, rest, offen, anzeige,
      status: k.gesperrt === "automatisch" ? "automatisch gesperrt" : k.gesperrt === "von Hand" ? "von Hand gesperrt" : "frei",
    };
  });
}
